import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { CtySpinner } from '../spinner/CtySpinner';
import { useDitaHtmlProcessor } from '../hooks/useDitaHtmlProcessor';
import { Icon } from '@iconify/react';
import { prepareDitaHtml } from '../utils/ditaHtmlSanitizer';
import { renderDitaHtmlIntoContainer } from '../utils/markdownRenderUtils';
import { DITA_DEFAULT_CONTENT_STYLES } from '../utils/ditaHtmlProcessor';

interface DitaHtmlViewerProps {
  htmlUrl?: string;
  externalHtmlContent?: string; // For directly providing HTML string
  getPdfLink: (payload: any) => Promise<any>;
  advisorId?: string; // Required for API calls
  lazyLoad?: boolean;
  lazyLoadRootMargin?: string;
  concurrencyLimit?: number;
  onError?: (error: Error, sourceId?: string) => void;
  className?: string;
  enableSanitization?: boolean; // Enable/disable HTML sanitization (default: true)
  maxHtmlSizeMB?: number; // Maximum HTML size in MB (default: 10)
  maxDomNodes?: number; // Maximum DOM nodes (default: 10000)
}

/**
 * DitaHtmlViewer Component
 * 
 * Renders DITA HTML content and automatically processes data-dita-source_id references:
 * 1. Fetches the HTML content from htmlUrl (or uses externalHtmlContent)
 * 2. Extracts all data-dita-source_id attributes from <img> and <a> tags
 * 3. Calls the knowledge API to get presigned URLs for each source_id
 * 4. Replaces src (for images) and href (for anchors) with the presigned URLs
 * 5. Supports lazy loading for better performance
 */
export function DitaHtmlViewer({
  htmlUrl,
  externalHtmlContent,
  getPdfLink,
  advisorId,
  lazyLoad = true,
  lazyLoadRootMargin = '200px',
  concurrencyLimit = 5,
  onError,
  className = '',
  enableSanitization = true,
  maxHtmlSizeMB = 10,
  maxDomNodes = 10000,
}: DitaHtmlViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(!!htmlUrl);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [shouldStartProcessing, setShouldStartProcessing] = useState(false);
  const htmlRenderedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const processHtmlContent = useCallback((rawHtml: string): boolean => {
    try {
      setHtmlContent(prepareDitaHtml(rawHtml, { enableSanitization, maxHtmlSizeMB, maxDomNodes }));
      return true;
    } catch (error) {
      setFetchError((error as Error).message || 'Failed to process HTML content');
      console.error('❌ Failed to process HTML:', error);
      onErrorRef.current?.(error as Error);
      return false;
    }
  }, [enableSanitization, maxHtmlSizeMB, maxDomNodes]);

  // Fetch HTML from URL if provided
  useEffect(() => {
    if (!htmlUrl) {
      setIsLoading(false);
      return;
    }

    // Cancel previous request if any
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchHtml = async () => {
      try {
        htmlRenderedRef.current = false;
        setShouldStartProcessing(false);
        setIsLoading(true);
        setFetchError(null);

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        const response = await fetch(htmlUrl, {
          signal: abortControllerRef.current.signal,
          headers: {
            'Accept': 'text/html, application/xhtml+xml',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch HTML: ${response.status} ${response.statusText}`);
        }

        const html = await response.text();
        console.log('✅ Loaded DITA HTML successfully from:', htmlUrl.substring(0, 100) + '...');

        // Process and validate the HTML
        processHtmlContent(html);
      } catch (error: any) {
        // Ignore abort errors
        if (error.name === 'AbortError') {
          console.log('🚫 Fetch aborted');
          return;
        }

        const errorMessage = error?.message || 'Failed to load HTML content';
        setFetchError(errorMessage);
        console.error('❌ Failed to load DITA HTML:', error);
        onErrorRef.current?.(error as Error);
      } finally {
        setIsLoading(false);
        abortControllerRef.current = null;
      }
    };

    fetchHtml();

    // Cleanup function to abort fetch on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [htmlUrl, processHtmlContent]);

  // Update content when external content changes
  useEffect(() => {
    if (externalHtmlContent) {
      processHtmlContent(externalHtmlContent);
      setIsLoading(false);
      setFetchError(null);
    }
  }, [externalHtmlContent, processHtmlContent]);

  // Process DITA source references once HTML content is available and rendered in DOM
  // This should only run ONCE when HTML is first loaded
  useEffect(() => {
    if (!containerRef.current || !htmlContent || shouldStartProcessing) {
      return;
    }

    console.log('🔍 DitaHtmlViewer: HTML content loaded, checking for data-dita-source_id attributes...');

    // Small delay to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      const container = containerRef.current;
      if (container) {
        const allElements = container.querySelectorAll('[data-dita-source_id]');
        console.log(`📋 DitaHtmlViewer: Found ${allElements.length} elements with data-dita-source_id attribute`);

        if (allElements.length > 0) {
          console.log('✅ DitaHtmlViewer: Initiating DITA processing (ONE TIME ONLY)...');
          setShouldStartProcessing(true); // This will trigger the hook
        } else {
          console.warn('⚠️ DitaHtmlViewer: No elements with data-dita-source_id found.');
        }
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [htmlContent, shouldStartProcessing]);

  // Use a stable reference - only set HTML once, then let our processing update it
  useLayoutEffect(() => {
    if (isLoading || !containerRef.current || !htmlContent || htmlRenderedRef.current) {
      return;
    }

    try {
      renderDitaHtmlIntoContainer(containerRef.current, htmlContent);
      htmlRenderedRef.current = true;
      console.log('📄 DitaHtmlViewer: HTML rendered into DOM');
    } catch (error) {
      console.error('❌ Failed to render HTML into DOM:', error);
      setFetchError('Failed to render HTML content');
      onErrorRef.current?.(error as Error);
    }
  }, [htmlContent, isLoading]);

  // Process DITA source references - this hook will run when shouldStartProcessing changes
  useDitaHtmlProcessor(containerRef, {
    getPdfLink,
    advisorId,
    lazyLoad,
    lazyLoadRootMargin,
    concurrencyLimit,
    onError,
    trigger: shouldStartProcessing, // Trigger once when processing should start
  });

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center h-full ${className}`}>
        <div className="flex flex-col items-center gap-3">
          <CtySpinner size="lg" />
          <p className="text-sm text-default-500">Loading DITA content...</p>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div
        className={`flex flex-col items-center justify-center h-full text-danger ${className}`}
      >
        <Icon icon="mdi:alert-circle" className="w-12 h-12 mb-2" />
        <p className="text-lg font-medium">Failed to Load DITA Content</p>
        <p className="text-sm mt-2">{fetchError}</p>
      </div>
    );
  }

  if (!htmlContent) {
    return (
      <div className={`flex items-center justify-center h-full text-default-500 ${className}`}>
        <p className="text-lg">No DITA HTML content to display.</p>
      </div>
    );
  }

  return (
    <>
      <div
        ref={containerRef}
        className={`dita-html-viewer dita-content h-full w-full ${className}`}
      />
      <style>{DITA_DEFAULT_CONTENT_STYLES}</style>
    </>
  );
}

